import { ReactNode, useCallback } from 'react';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import Button from 'src/components/primitives/Button/Button';
import Checkbox from 'src/components/primitives/Checkbox/Checkbox';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import { WithChildren } from 'src/utils/UtilityTypes';

interface Props extends WithChildren {
	tradeSide: 'buy' | 'sell';
	isSubmitting: boolean;
	handleConfirmation: () => void;
	handleClose: () => void;
	title?: ReactNode;
	reduceOnly?: boolean;
}

export default function TradeConfirmationModalWrapper({
	tradeSide,
	isSubmitting,
	handleConfirmation,
	handleClose,
	reduceOnly,
	title,
	children,
}: Props) {
	const [{ showTradeConfirmation }, setCurrentSettings] = useCurrentSettings();

	const handleToggleShowTradeConfirmation = useCallback(() => {
		setCurrentSettings({
			showTradeConfirmation: !showTradeConfirmation,
		});
	}, [setCurrentSettings, showTradeConfirmation]);

	return (
		<ResponsiveModal.Container
			onClose={handleClose}
			dataTestId="trade-confirmation-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="trade-confirmation-modal-close">
				{title ?? 'Confirm order'}
			</ResponsiveModal.Title>
			<ResponsiveModal.Body>{children}</ResponsiveModal.Body>
			<ResponsiveModal.Footer className="flex-col items-stretch">
				<Checkbox.Default
					id="trade-confirmation-dont-show-again"
					label="Don't show this again"
					checked={!showTradeConfirmation}
					onCheckedChange={handleToggleShowTradeConfirmation}
					className="w-fit"
					dataTestId="trade-confirmation-dont-show-checkbox"
				/>
				<Button
					size="lg"
					variant={tradeSide === 'buy' ? 'positive' : 'negative'}
					disabled={isSubmitting}
					isLoading={isSubmitting}
					onClick={handleConfirmation}
					dataTestId="trade-confirmation-modal-submit"
				>
					{reduceOnly ? 'Reduce position' : 'Confirm'}
				</Button>
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
}
